import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, CheckCircle, Plus, Loader2, ChevronDown, ChevronUp, Sparkles, UserPlus, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import CreateTaskModal from '../tasks/CreateTaskModal';
import AssignTaskModal from '../tasks/AssignTaskModal';
import BulkTaskGenerationModal from '../tasks/BulkTaskGenerationModal';

const API = '/api';

/**
 * PlanView - World Class Minimalist Design
 */
const PlanView = ({ caseId, currentUserId }: { caseId: string; currentUserId?: string }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'no';
  const isEnglish = currentLanguage === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [memberWorkloads, setMemberWorkloads] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assigningTask, setAssigningTask] = useState(null);
  const [expandedMember, setExpandedMember] = useState(null);
  const [showBulkGenModal, setShowBulkGenModal] = useState(false);

  const statusLabelsEn = {
    'TODO': 'To Do',
    'IN_PROGRESS': 'In Progress',
    'COMPLETED': 'Completed',
    'BLOCKED': 'Blocked',
    'PENDING': 'Pending',
    'pending': 'Pending',
    'in_progress': 'In Progress',
    'completed': 'Completed',
    'blocked': 'Blocked',
    'todo': 'To Do'
  };

  const statusLabelsNo = {
    'TODO': 'Å gjøre',
    'IN_PROGRESS': 'Pågår',
    'COMPLETED': 'Fullført',
    'BLOCKED': 'Blokkert',
    'PENDING': 'Venter',
    'pending': 'Venter',
    'in_progress': 'Pågår',
    'completed': 'Fullført',
    'blocked': 'Blokkert',
    'todo': 'Å gjøre'
  };

  const statusLabels = isEnglish ? statusLabelsEn : statusLabelsNo;

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [tasksRes, membersRes] = await Promise.all([
        axios.get(`${API}/tasks/${caseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/cases/${caseId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const tasksList = tasksRes.data.tasks || [];
      const membersList = membersRes.data.members || [];

      setTasks(tasksList);
      setMembers(membersList);
      calculateWorkloads(tasksList, membersList);
    } catch (error) {
      console.error('Failed to fetch plan data:', error);
      toast.error('Kunne ikke laste plandata');
    } finally {
      setLoading(false);
    }
  };

  const calculateWorkloads = (tasksList, membersList) => {
    const workloads = {};
    
    membersList.forEach(member => {
      const userTasks = tasksList.filter(t => {
        if (t.assignee_user_id !== member.user_id) return false;
        const status = (t.status || '').toUpperCase();
        return ['TODO', 'IN_PROGRESS', 'PENDING', 'PENDING_APPROVAL'].includes(status);
      });
      
      workloads[member.user_id] = {
        tasks: userTasks,
        total_hours: userTasks.reduce((sum: number, t: any) => sum + (t.estimated_hours || 0), 0),
        upcoming_deadline: userTasks
          .filter((t: any) => t.due_date)
          .sort((a: any, b: any) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())[0]
      };
    });

    setMemberWorkloads(workloads);
  };

  const handleTaskAssigned = async (taskId: string, userId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/tasks/${taskId}/assign`,
        { assignee_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('Task assigned!', 'Oppgave tildelt!'));
      setAssigningTask(null);
      fetchData();
    } catch (error) {
      console.error('Assign error:', error);
      toast.error(t('Could not assign task', 'Kunne ikke tildele oppgave'));
    }
  };

  const handleAutoAssignTasks = async () => {
    try {
      const unassignedTasks = tasks.filter(t => !t.assignee_user_id);
      if (unassignedTasks.length === 0 || members.length === 0) {
        toast.info(t('No tasks to distribute', 'Ingen oppgaver å fordele'));
        return;
      }

      const token = localStorage.getItem('token');
      
      const sortedMembers = [...members].sort((a, b) => {
        const workloadA = memberWorkloads[a.user_id]?.total_hours || 0;
        const workloadB = memberWorkloads[b.user_id]?.total_hours || 0;
        return workloadA - workloadB;
      });

      let memberIndex = 0;
      const assignments = [];

      for (const task of unassignedTasks) {
        const assignee = sortedMembers[memberIndex % sortedMembers.length];
        assignments.push({
          taskId: task.id,
          userId: assignee.user_id
        });
        memberIndex++;
      }

      await Promise.all(
        assignments.map(({ taskId, userId }) =>
          axios.patch(
            `${API}/tasks/${taskId}/assign`,
            { assignee_user_id: userId },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      toast.success(isEnglish ? `${assignments.length} tasks auto-assigned!` : `${assignments.length} oppgaver automatisk fordelt!`);
      fetchData();
    } catch (error) {
      console.error('Auto-assign error:', error);
      toast.error(t('Could not auto-assign tasks', 'Kunne ikke auto-fordele oppgaver'));
    }
  };

  const getWorkloadStatus = (hours) => {
    if (hours > 40) return { status: 'overloaded', color: 'bg-red-500', label: t('Overloaded', 'Overbelastet') };
    if (hours > 20) return { status: 'busy', color: 'bg-amber-500', label: t('High', 'Høy') };
    if (hours > 10) return { status: 'moderate', color: 'bg-emerald-500', label: t('Moderate', 'Moderat') };
    return { status: 'available', color: 'bg-gray-300', label: t('Available', 'Ledig') };
  };

  const groupedTasks = {
    todo: tasks.filter(t => t.status === 'TODO'),
    inProgress: tasks.filter(t => t.status === 'IN_PROGRESS'),
    completed: tasks.filter(t => t.status === 'COMPLETED')
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    if (a.due_date && b.due_date) {
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    }
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return 0;
  });

  const totalEstimate = tasks
    .filter((t: any) => ['TODO', 'IN_PROGRESS'].includes(t.status))
    .reduce((sum: number, t: any) => sum + (t.estimated_hours || 0), 0);

  if (loading) {
    return (
      <div className="min-h-full bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-5xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{t('Resource Plan', 'Ressursplan')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('Overview of tasks, estimates and team capacity', 'Oversikt over oppgaver, estimater og teamkapasitet')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {tasks.length < 3 && (
                <Button
                  onClick={() => setShowBulkGenModal(true)}
                  variant="outline"
                  className="rounded-xl"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('AI-generate', 'AI-generer')}
                </Button>
              )}
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gray-900 hover:bg-gray-800 rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('New task', 'Ny oppgave')}
              </Button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-12 py-5 border-y border-gray-100">
            <div>
              <div className="text-2xl font-semibold text-gray-900">{tasks.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Total', 'Totalt')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">{groupedTasks.todo.length + groupedTasks.inProgress.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Remaining', 'Gjenstående')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-emerald-600">{groupedTasks.completed.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Completed', 'Fullført')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">{totalEstimate.toFixed(1)}{t('h', 't')}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Estimated', 'Estimert')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">{members.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Team members', 'Teammedlemmer')}</div>
            </div>
          </div>
        </header>

        {/* Team Capacity */}
        {members.length > 0 && (
          <section className="mb-8">
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
              {t('TEAM CAPACITY', 'Team kapasitet')}
            </h2>
            <div className="space-y-0">
              {members.map(member => {
                const workload = memberWorkloads[member.user_id] || { tasks: [], total_hours: 0 };
                const status = getWorkloadStatus(workload.total_hours);
                const isExpanded = expandedMember === member.user_id;

                return (
                  <div
                    key={member.user_id}
                    className="group py-3 border-b border-gray-50 last:border-b-0"
                  >
                    <div 
                      className="flex items-center gap-4 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg py-2"
                      onClick={() => setExpandedMember(isExpanded ? null : member.user_id)}
                    >
                      {member.avatar_url || member.avatarUrl ? (
                        <img 
                          src={member.avatar_url || member.avatarUrl} 
                          alt={member.user_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-sm font-medium text-white">
                          {member.user_name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm text-gray-900">{member.user_name}</h4>
                        <p className="text-xs text-gray-400">{member.role}</p>
                      </div>
                      <div className="text-right mr-4">
                        <p className="text-sm font-medium text-gray-900">{workload.total_hours.toFixed(1)}{t('h', 't')}</p>
                        <p className="text-xs text-gray-400">{workload.tasks.length} {t('tasks', 'oppgaver')}</p>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${status.color}`} title={status.label} />
                      {workload.tasks.length > 0 && (
                        isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>

                    {/* Expanded Tasks */}
                    {isExpanded && workload.tasks.length > 0 && (
                      <div className="ml-14 mt-2 space-y-1">
                        {workload.tasks.map(task => (
                          <div key={task.id} className="flex items-center justify-between py-1.5 text-sm">
                            <span className="text-gray-600 truncate flex-1">{task.title}</span>
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                              {task.estimated_hours && <span>{task.estimated_hours}{t('h', 't')}</span>}
                              {task.due_date && (
                                <span>{new Date(task.due_date).toLocaleDateString(isEnglish ? 'en-US' : 'nb-NO', { day: 'numeric', month: 'short' })}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Task Timeline */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              {t('TASKS', 'Oppgaver')}
            </h2>
            {tasks.length > 0 && tasks.some(t => !t.assignee_user_id) && members.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAutoAssignTasks}
                className="text-xs rounded-lg"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                {t('Auto-assign', 'Auto-fordel')}
              </Button>
            )}
          </div>
          
          {sortedTasks.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-900 font-medium mb-1">{t('No tasks yet', 'Ingen oppgaver ennå')}</p>
              <p className="text-sm text-gray-500 mb-4">
                {t('Let AI generate a complete task plan', 'La AI generere en komplett oppgaveplan')}
              </p>
              <Button
                onClick={() => setShowBulkGenModal(true)}
                className="bg-gray-900 hover:bg-gray-800 rounded-xl"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('Auto-generate tasks', 'Auto-generer oppgaver')}
              </Button>
            </div>
          ) : (
            <div className="space-y-0">
              {sortedTasks.map(task => {
                const assignee = members.find(m => m.user_id === task.assignee_user_id);
                const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'COMPLETED';

                return (
                  <div 
                    key={task.id}
                    className={`group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg ${
                      isOverdue ? 'bg-red-50' : ''
                    }`}
                  >
                    {/* Status indicator */}
                    <div className={`w-0.5 h-8 rounded-full flex-shrink-0 ${
                      task.status === 'COMPLETED' ? 'bg-gray-300' :
                      task.status === 'IN_PROGRESS' ? 'bg-violet-400' :
                      isOverdue ? 'bg-red-400' :
                      'bg-amber-400'
                    }`} />
                    
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium text-sm ${task.status === 'COMPLETED' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {task.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                        {task.due_date && (
                          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            {new Date(task.due_date).toLocaleDateString(isEnglish ? 'en-US' : 'nb-NO', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                        {task.estimated_hours && <span>{task.estimated_hours}{t('h', 't')}</span>}
                      </div>
                    </div>
                    
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      task.status === 'COMPLETED' ? 'bg-gray-100 text-gray-600' :
                      task.status === 'IN_PROGRESS' ? 'bg-violet-100 text-violet-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {statusLabels[task.status] || task.status}
                    </span>
                    
                    {assignee ? (
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{assignee.user_name}</p>
                        <p className="text-xs text-gray-400">{assignee.role}</p>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAssigningTask(task)}
                        className="text-xs rounded-lg"
                      >
                        {t('Assign', 'Tildel')}
                      </Button>
                    )}
                    
                    <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* AI Suggestion */}
        {tasks.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-medium text-gray-400">{t('Lexy suggests', 'Lexy foreslår')}</span>
              </div>
              <p className="text-sm text-gray-300">
                {tasks.filter(t => !t.assignee_user_id).length > 0 
                  ? (isEnglish 
                      ? `You have ${tasks.filter(t => !t.assignee_user_id).length} unassigned tasks. Use auto-assign to balance the workload.`
                      : `Du har ${tasks.filter(t => !t.assignee_user_id).length} utildelte oppgaver. Bruk auto-fordeling for å balansere arbeidsmengden.`)
                  : (isEnglish
                      ? `Great work! All tasks are assigned. Monitor deadlines and progress.`
                      : `Bra jobbet! Alle oppgaver er tildelt. Følg med på frister og progresjon.`)
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateTaskModal
          caseId={caseId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />
      )}

      {assigningTask && (
        <AssignTaskModal
          caseId={caseId}
          task={assigningTask}
          onClose={() => setAssigningTask(null)}
          onAssign={(userId) => handleTaskAssigned(assigningTask.id, userId)}
        />
      )}

      {showBulkGenModal && (
        <BulkTaskGenerationModal
          caseId={caseId}
          onClose={() => setShowBulkGenModal(false)}
          onTasksCreated={() => {
            setShowBulkGenModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default PlanView;
